import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { MarketSidebar } from '../components/layout/MarketSidebar';
import { Loading, Modal, ScoreBadge } from '../components/common';
import { usePreferences, calculatePersonalScore, type CommuteDestination } from '../contexts';
import { formatCurrency } from '../utils/formatters';
import type { Property } from '../types';
import './PersonalScorerPage.css';

interface ScoredProperty extends Property {
  personalScore: number;
  scoreBreakdown: Record<string, number>;
  matches: string[];
  issues: string[];
}

export function PersonalScorerPage() {
  const [searchParams] = useSearchParams();
  const initialMarket = searchParams.get('market');

  const [selectedMarkets, setSelectedMarkets] = useState<string[]>(() => {
    if (initialMarket) return [initialMarket];
    const saved = localStorage.getItem('selectedMarkets');
    return saved ? JSON.parse(saved) : [];
  });

  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<ScoredProperty | null>(null);

  const {
    preferences,
    updatePreferences,
    updateWeight,
    addCommute,
    removeCommute,
    updateCommute,
    resetToDefaults,
  } = usePreferences();

  // Load properties
  useEffect(() => {
    const loadProperties = async () => {
      if (selectedMarkets.length === 0) {
        setProperties([]);
        return;
      }

      setLoading(true);
      try {
        const allProps: Property[] = [];
        for (const market of selectedMarkets) {
          const data = await api.getMarketProperties(market);
          const props = (data.properties || []).map((p) => ({
            ...p,
            market,
            dom: p.daysOnMarket ?? p.dom,
            score: p.investmentScore ?? p.score ?? p.finalScore ?? 50,
          }));
          allProps.push(...props);
        }
        setProperties(allProps);
      } catch (err) {
        console.error('Failed to load properties:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProperties();
  }, [selectedMarkets]);

  // Calculate personal scores for all properties
  const scoredProperties = useMemo(() => {
    return properties
      .map((p): ScoredProperty => {
        const { score, breakdown, matches, issues } = calculatePersonalScore(
          {
            ...p,
            pricePerSqft: p.sqft ? p.price / p.sqft : undefined,
          },
          preferences
        );
        return {
          ...p,
          personalScore: score,
          scoreBreakdown: breakdown,
          matches,
          issues,
        };
      })
      .filter((p) => p.price >= preferences.budgetMin * 0.8 && p.price <= preferences.budgetMax * 1.2)
      .sort((a, b) => b.personalScore - a.personalScore);
  }, [properties, preferences]);

  const stats = useMemo(() => {
    const count = scoredProperties.length;
    const avgScore =
      count > 0 ? scoredProperties.reduce((sum, p) => sum + p.personalScore, 0) / count : 0;
    const avgPrice =
      count > 0 ? scoredProperties.reduce((sum, p) => sum + p.price, 0) / count : 0;
    const highMatches = scoredProperties.filter((p) => p.personalScore >= 70).length;
    return { count, avgScore, avgPrice, highMatches };
  }, [scoredProperties]);

  const totalWeight = Object.values(preferences.weights).reduce((a, b) => a + b, 0);

  return (
    <div className="page-with-sidebar">
      <MarketSidebar selectedMarkets={selectedMarkets} onSelectionChange={setSelectedMarkets} />

      <div className="main-content">
        <div className="container">
          <div className="page-header">
            <div>
              <h1>Personal Home Scorer</h1>
              <p>Find your perfect home based on your preferences</p>
            </div>
            <button className="btn-preferences" onClick={() => setShowPreferences(true)}>
              Configure Preferences
            </button>
          </div>

          {/* Stats Bar */}
          {selectedMarkets.length > 0 && (
            <div className="stats-bar personal-stats">
              <div className="stat-item">
                <span className="stat-value">{stats.count}</span>
                <span className="stat-label">Properties</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{stats.avgScore.toFixed(0)}</span>
                <span className="stat-label">Avg Match</span>
              </div>
              <div className="stat-item highlight">
                <span className="stat-value">{stats.highMatches}</span>
                <span className="stat-label">Good Matches (70+)</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{formatCurrency(stats.avgPrice)}</span>
                <span className="stat-label">Avg Price</span>
              </div>
            </div>
          )}

          {/* Budget display */}
          <div className="budget-display">
            <span>Budget: </span>
            <strong>
              {formatCurrency(preferences.budgetMin)} - {formatCurrency(preferences.budgetMax)}
            </strong>
          </div>

          {/* Property List */}
          {loading ? (
            <Loading message="Loading properties..." />
          ) : scoredProperties.length === 0 ? (
            <div className="no-properties">
              <h3>No Properties Found</h3>
              <p>
                {selectedMarkets.length === 0
                  ? 'Select markets from the sidebar'
                  : 'No properties match your budget range'}
              </p>
            </div>
          ) : (
            <div className="personal-table-container">
              <table className="personal-table">
                <thead>
                  <tr>
                    <th>Address</th>
                    <th>Price</th>
                    <th>Bd</th>
                    <th>Ba</th>
                    <th>Sqft</th>
                    <th>Year</th>
                    <th>DOM</th>
                    <th>Matches</th>
                    <th>Issues</th>
                    <th>Match</th>
                    <th>Inv</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {scoredProperties.map((property, index) => (
                    <PersonalPropertyRow
                      key={`${property.address}-${index}`}
                      property={property}
                      onClick={() => setSelectedProperty(property)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Preferences Modal */}
      <Modal
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
        title="Your Preferences"
      >
        <div className="preferences-form">
          {/* Budget */}
          <div className="pref-section">
            <h4>Budget Range</h4>
            <div className="budget-inputs">
              <label>
                Min
                <input
                  type="number"
                  value={preferences.budgetMin}
                  onChange={(e) => updatePreferences({ budgetMin: Number(e.target.value) })}
                  step={10000}
                />
              </label>
              <span className="budget-separator">to</span>
              <label>
                Max
                <input
                  type="number"
                  value={preferences.budgetMax}
                  onChange={(e) => updatePreferences({ budgetMax: Number(e.target.value) })}
                  step={10000}
                />
              </label>
            </div>
          </div>

          {/* Minimum Requirements */}
          <div className="pref-section">
            <h4>Minimum Requirements</h4>
            <div className="req-grid">
              <label>
                Beds
                <input
                  type="number"
                  value={preferences.minBeds}
                  onChange={(e) => updatePreferences({ minBeds: Number(e.target.value) })}
                  min={1}
                  max={6}
                />
              </label>
              <label>
                Baths
                <input
                  type="number"
                  value={preferences.minBaths}
                  onChange={(e) => updatePreferences({ minBaths: Number(e.target.value) })}
                  min={1}
                  max={5}
                  step={0.5}
                />
              </label>
              <label>
                Sqft
                <input
                  type="number"
                  value={preferences.minSqft}
                  onChange={(e) => updatePreferences({ minSqft: Number(e.target.value) })}
                  step={100}
                />
              </label>
              <label>
                Max Commute
                <input
                  type="number"
                  value={preferences.maxCommute}
                  onChange={(e) => updatePreferences({ maxCommute: Number(e.target.value) })}
                  min={5}
                  max={90}
                />
                <span className="input-hint">minutes</span>
              </label>
            </div>
          </div>

          {/* Commute Destinations */}
          <div className="pref-section">
            <h4>Commute Destinations</h4>
            {preferences.commutes.map((commute, index) => (
              <div key={index} className="commute-row">
                <input
                  type="text"
                  placeholder="Name"
                  value={commute.name}
                  onChange={(e) => updateCommute(index, { name: e.target.value })}
                  className="commute-name"
                />
                <input
                  type="text"
                  placeholder="Address"
                  value={commute.address}
                  onChange={(e) => updateCommute(index, { address: e.target.value })}
                  className="commute-address"
                />
                <select
                  value={commute.importance}
                  onChange={(e) =>
                    updateCommute(index, { importance: e.target.value as CommuteDestination['importance'] })
                  }
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <button className="btn-remove" onClick={() => removeCommute(index)}>
                  &times;
                </button>
              </div>
            ))}
            <button
              className="btn-add-commute"
              onClick={() => addCommute({ name: '', address: '', importance: 'medium' })}
            >
              + Add Destination
            </button>
          </div>

          {/* Scoring Weights */}
          <div className="pref-section">
            <h4>
              Scoring Weights{' '}
              <span className={`weight-total ${totalWeight !== 100 ? 'warning' : ''}`}>
                (Total: {totalWeight}%)
              </span>
            </h4>
            <div className="weights-grid">
              {Object.entries(preferences.weights).map(([key, value]) => (
                <div key={key} className="weight-item">
                  <label>{key.charAt(0).toUpperCase() + key.slice(1)}</label>
                  <input
                    type="range"
                    min={0}
                    max={30}
                    value={value}
                    onChange={(e) =>
                      updateWeight(key as keyof typeof preferences.weights, Number(e.target.value))
                    }
                  />
                  <span className="weight-value">{value}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="pref-actions">
            <button className="btn-reset" onClick={resetToDefaults}>
              Reset to Defaults
            </button>
            <button className="btn-done" onClick={() => setShowPreferences(false)}>
              Done
            </button>
          </div>
        </div>
      </Modal>

      {/* Property Detail Modal */}
      <Modal
        isOpen={selectedProperty !== null}
        onClose={() => setSelectedProperty(null)}
        title="Match Analysis"
      >
        {selectedProperty && <PropertyMatchDetail property={selectedProperty} />}
      </Modal>
    </div>
  );
}

function PersonalPropertyRow({
  property,
  onClick,
}: {
  property: ScoredProperty;
  onClick: () => void;
}) {
  const scoreClass =
    property.personalScore >= 70
      ? 'score-high'
      : property.personalScore >= 50
        ? 'score-medium'
        : 'score-low';

  return (
    <tr onClick={onClick} className="clickable-row">
      <td className="col-address">{property.address}</td>
      <td className="col-price">{formatCurrency(property.price)}</td>
      <td className="col-num">{property.beds ?? '-'}</td>
      <td className="col-num">{property.baths ?? '-'}</td>
      <td className="col-num">{property.sqft?.toLocaleString() ?? '-'}</td>
      <td className="col-num">{property.yearBuilt ?? '-'}</td>
      <td className="col-num">{property.dom ?? '-'}</td>
      <td className="col-tags">
        {property.matches.slice(0, 2).map((m, i) => (
          <span key={i} className="match-tag">{m}</span>
        ))}
      </td>
      <td className="col-tags">
        {property.issues.slice(0, 2).map((m, i) => (
          <span key={i} className="issue-tag">{m}</span>
        ))}
      </td>
      <td className="col-score">
        <span className={`personal-score-badge ${scoreClass}`}>{property.personalScore}</span>
      </td>
      <td className="col-score">
        {property.score !== undefined && <ScoreBadge score={property.score} />}
      </td>
      <td className="col-link">
        {property.url && (
          <a
            href={property.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            View
          </a>
        )}
      </td>
    </tr>
  );
}

function PropertyMatchDetail({ property }: { property: ScoredProperty }) {
  const scoreClass =
    property.personalScore >= 70
      ? 'score-high'
      : property.personalScore >= 50
        ? 'score-medium'
        : 'score-low';

  return (
    <div className="match-detail">
      <div className="match-header">
        <div>
          <span className="match-price">{formatCurrency(property.price)}</span>
          <div className="match-address">{property.address}</div>
          <div className="match-basics">
            {property.beds != null && <span>{property.beds} bd</span>}
            {property.baths != null && <span>{property.baths} ba</span>}
            {property.sqft != null && <span>{property.sqft.toLocaleString()} sqft</span>}
            {property.yearBuilt != null && <span>Built {property.yearBuilt}</span>}
          </div>
        </div>
        <div className={`match-score-big ${scoreClass}`}>{property.personalScore}</div>
      </div>

      {/* Score Breakdown */}
      <div className="match-section">
        <h4>Score Breakdown</h4>
        <div className="breakdown-bars">
          {Object.entries(property.scoreBreakdown).map(([key, value]) => (
            <div key={key} className="breakdown-row">
              <span className="breakdown-label">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
              <div className="breakdown-bar-container">
                <div
                  className={`breakdown-bar ${value >= 70 ? 'bar-high' : value >= 50 ? 'bar-medium' : 'bar-low'}`}
                  style={{ width: `${value}%` }}
                />
              </div>
              <span className="breakdown-value">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Matches */}
      {property.matches.length > 0 && (
        <div className="match-section">
          <h4>What You'll Love</h4>
          <ul className="match-list positive">
            {property.matches.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Issues */}
      {property.issues.length > 0 && (
        <div className="match-section">
          <h4>Things to Consider</h4>
          <ul className="match-list negative">
            {property.issues.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Investment Score */}
      <div className="match-section comparison">
        <div className="comparison-row">
          <span>Personal Match Score:</span>
          <strong className={scoreClass}>{property.personalScore}</strong>
        </div>
        {property.score !== undefined && (
          <div className="comparison-row">
            <span>Investment Score:</span>
            <strong>{property.score}</strong>
          </div>
        )}
      </div>

      {/* Link */}
      {property.url && (
        <a href={property.url} target="_blank" rel="noopener noreferrer" className="match-link">
          View Listing
        </a>
      )}
    </div>
  );
}
